/**
 * Phase O — Per-section Mobile SFG Pick.
 *
 * Mobile-optimised flow for `/sections/dispatch/:section/mobile/sfg-pick`.
 * Uses the simple per-section SFG pick API
 * (`/api/per-section/{slug}/sfg-pick/...`) — same as
 * `PerSectionSfgPickClone.jsx` (Phase D6). Salem's `MobileSFGPicking.jsx`
 * is paired with this file in `SALEM_CLONE_DRIFT.md` row 9.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Package, MapPin, CheckCircle, RotateCcw, AlertTriangle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const PerSectionMobileSfgPick = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const mobileHome = `/sections/dispatch/${slug}/mobile`;
  const apiBase = `/per-section/${slug}/sfg-pick`;

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  const [step, setStep] = useState('staff'); // staff, scan, confirm, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [binDetails, setBinDetails] = useState(null);
  const [pickSlipNo, setPickSlipNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [pickCount, setPickCount] = useState(0);

  useEffect(() => {
    if (step === 'staff') setTimeout(() => staffInputRef.current?.focus(), 100);
    else if (step === 'scan') setTimeout(() => bundleInputRef.current?.focus(), 100);
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffName(response.data.staff_name);
        setStep('scan');
        vibrate([50]);
        toast.success(`Welcome, ${response.data.staff_name}!`);
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan bundle barcode');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(
        `${apiBase}/validate-bundle/${bundleCode}?staff_id=${encodeURIComponent(staffId)}`
      );
      setBundleDetails(response.data.bundle);
      setBinDetails(response.data.bin || null);
      setStep('confirm');
      vibrate([50]);
      toast.success(
        response.data.bundle?.bin_location
          ? `Bundle in bin ${response.data.bundle.bin_location}`
          : 'Bundle ready to pick'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle not found');
      vibrate([100, 50, 100]);
      setBundleCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPick = async () => {
    if (!bundleDetails) {
      toast.error('Scan a bundle first');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      await apiClient.post(apiBase, {
        staff_id: staffId,
        bundle_code: bundleCode,
        pick_slip_no: pickSlipNo || undefined,
      });
      setPickCount((p) => p + 1);
      setStep('success');
      vibrate([50, 100, 50]);
      toast.success('Pick complete!');
      setTimeout(resetForNextBundle, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pick failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const resetForNextBundle = () => {
    setBundleCode('');
    setBundleDetails(null);
    setBinDetails(null);
    setPickSlipNo('');
    setStep('scan');
  };

  const resetAll = () => {
    setStaffId('');
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setBinDetails(null);
    setPickSlipNo('');
    setPickCount(0);
    setStep('staff');
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  if (!sectionName) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-6">
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-400 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

  const stepOrder = ['staff', 'scan', 'confirm', 'success'];
  const currentStepIdx = stepOrder.indexOf(step);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid={`per-section-mobile-sfg-pick-${slug}`}>
      <header className="bg-gradient-to-r from-cyan-600 to-cyan-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(mobileHome)}
            className="text-white hover:bg-white/20 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">SFG Picking</h1>
            <p className="text-xs text-cyan-200">{sectionName} Section{staffName ? ` · ${staffName}` : ''}</p>
          </div>
          <div className="w-10" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          {stepOrder.map((s, i) => (
            <div key={s} className={`flex-1 h-2 rounded-full ${i <= currentStepIdx ? 'bg-white' : 'bg-white/30'}`} />
          ))}
        </div>
      </header>

      {step !== 'staff' && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-400">Session Picks:</span>
          <span className="font-bold text-cyan-400" data-testid="session-counter">{pickCount}</span>
        </div>
      )}

      <main className="flex-1 p-4 flex flex-col">
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-cyan-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Scan your badge to start picking</p>
              </div>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-16 text-xl font-bold bg-cyan-600 hover:bg-cyan-700 rounded-xl"
                data-testid="mobile-staff-validate-btn"
              >
                {loading ? 'Validating...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {step === 'scan' && (
          <div className="flex-1 flex flex-col justify-center space-y-4">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto">
                <Truck className="w-10 h-10 text-cyan-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Bundle</h2>
                <p className="text-gray-400">Scan the bundle barcode to pick</p>
              </div>
              <Input
                ref={bundleInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                placeholder="Bundle Barcode"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bundle-input"
              />
              <Input
                value={pickSlipNo}
                onChange={(e) => setPickSlipNo(e.target.value)}
                placeholder="Pick Slip No (optional)"
                className="h-12 text-base text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-pick-slip-input"
              />
              <Button
                onClick={handleBundleScan}
                disabled={loading || !bundleCode}
                className="w-full h-16 text-xl font-bold bg-cyan-600 hover:bg-cyan-700 rounded-xl"
                data-testid="mobile-bundle-validate-btn"
              >
                {loading ? 'Validating...' : 'Validate Bundle'}
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            {(bundleDetails.bin_location || binDetails?.bin_code) && (
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-5" data-testid="bin-location-card">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-7 h-7" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Go to Location</p>
                    <p className="text-3xl font-bold">{bundleDetails.bin_location || binDetails?.bin_code}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Pick Bundle</p>
                    <p className="font-mono font-bold text-lg" data-testid="bundle-code-display">{bundleCode}</p>
                    {bundleDetails.design_name && (
                      <p className="text-sm text-cyan-300">{bundleDetails.design_name}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{bundleDetails.usn_count ?? bundleDetails.total_pieces ?? '-'}</p>
                  <p className="text-xs text-gray-500">units</p>
                </div>
              </div>
            </div>

            <div className="mt-auto space-y-3">
              <Button
                onClick={handleConfirmPick}
                disabled={loading}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-confirm-pick-btn"
              >
                {loading ? 'Processing...' : 'Confirm Pick'}
              </Button>
              <Button
                variant="outline"
                onClick={resetForNextBundle}
                className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800 rounded-xl"
                data-testid="mobile-cancel-rescan-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Cancel & Rescan
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6" data-testid="mobile-success-card">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Picked!</h2>
                <p className="text-gray-400 mt-2">Bundle {bundleCode} picked</p>
              </div>
              <p className="text-sm text-gray-500">Ready for next bundle...</p>
            </div>
          </div>
        )}
      </main>

      {step !== 'staff' && step !== 'success' && (
        <footer className="bg-gray-800 px-4 py-3 safe-area-inset-bottom">
          <Button
            variant="ghost"
            onClick={resetAll}
            className="w-full text-gray-400 hover:text-white"
            data-testid="mobile-end-session-btn"
          >
            End Session
          </Button>
        </footer>
      )}
    </div>
  );
};

export default PerSectionMobileSfgPick;
